import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import type { EscrowDashboardData, DashboardDeadline } from '../../types';
import KpiStrip from './KpiStrip';
import DeadlineAgenda from './DeadlineAgenda';
import DeadlineCalendar from './DeadlineCalendar';
import DeadlineDocsPopover from './DeadlineDocsPopover';
import DealLane from './DealLane';
import { Segmented } from '../ui';

export default function EscrowDashboard() {
  const [data, setData] = useState<EscrowDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'by_deal' | 'agenda' | 'calendar'>('by_deal');
  const [docsFor, setDocsFor] = useState<DashboardDeadline | null>(null);
  const [briefing, setBriefing] = useState<string | null>(null);
  const [briefingBusy, setBriefingBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try { setData(await api.getEscrowDashboard()); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (d: DashboardDeadline) => {
    await api.updateDealDeadline(d.id, { is_completed: !d.is_completed });
    load();
  };
  const reschedule = async (d: DashboardDeadline, due_date: string) => {
    if (!due_date) return;
    setData((cur) => cur ? { ...cur, deadlines: cur.deadlines.map((x) => x.id === d.id ? { ...x, due_date } : x) } : cur);
    try { await api.updateDealDeadline(d.id, { due_date }); } finally { load(); }
  };

  const loadBriefing = async () => {
    setBriefingBusy(true);
    try { setBriefing((await api.getDeskBriefing()).briefing); }
    catch { setBriefing('Could not generate the briefing.'); }
    finally { setBriefingBusy(false); }
  };

  if (loading) return <div className="py-12 text-center text-[12.5px] text-gray-500">Loading dashboard...</div>;
  if (err) return <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-700">{err}</div>;
  if (!data) return null;
  if (data.deals.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-6 py-[30px] text-center text-[12.5px] text-gray-500">
        No deals in escrow. Promote an opportunity from the Opportunities board (Open Escrow).
      </div>
    );
  }

  return (
    <div>
      {/* Today's briefing: a card on a hairline with the design's red eyebrow; the one link is red */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white px-5 py-4">
        <div className="mb-2.5 flex items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[.13em] text-primary-500">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.9 5.8L20 10l-6.1 1.2L12 17l-1.9-5.8L4 10l6.1-1.2z" /></svg>
            Today's briefing
          </p>
          <button type="button" onClick={loadBriefing} disabled={briefingBusy} className="text-[11.5px] font-semibold leading-none text-primary-500 hover:text-primary-600 hover:underline underline-offset-[3px] disabled:opacity-50">{briefingBusy ? 'Thinking...' : briefing ? 'Refresh' : 'Generate'}</button>
        </div>
        {briefing
          ? <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-700">{briefing}</p>
          : <p className="text-[12.5px] text-gray-500">Generate a quick read on what needs you today.</p>}
      </div>
      <KpiStrip kpis={data.kpis} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Segmented
          aria-label="Escrow view"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'by_deal', label: 'By deal' },
            { value: 'agenda', label: 'Agenda' },
            { value: 'calendar', label: 'Calendar' },
          ]}
        />
        <span className="text-[12.5px] text-gray-500">{data.deals.length} deal{data.deals.length === 1 ? '' : 's'} in escrow</span>
      </div>
      {tab === 'by_deal' ? (
        <div className="space-y-4">
          {[...data.deals]
            .sort((a, b) => (b.risk.score - a.risk.score) || ((a.milestones.next?.days_until ?? 9999) - (b.milestones.next?.days_until ?? 9999)))
            .map((d) => <DealLane key={d.id} deal={d} deadlines={data.deadlines.filter((dl) => dl.deal_id === d.id)} onChanged={load} />)}
        </div>
      ) : tab === 'agenda' ? (
        <DeadlineAgenda deadlines={data.deadlines} onToggle={toggle} onReschedule={reschedule} onOpenDocs={setDocsFor} />
      ) : (
        <DeadlineCalendar deadlines={data.deadlines} onReschedule={reschedule} />
      )}
      {docsFor && (
        <DeadlineDocsPopover deadline={docsFor} onClose={() => setDocsFor(null)} onChanged={load} />
      )}
    </div>
  );
}
